// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

/* eslint-disable spire/fenced-imports */
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/autoplay";
import "swiper/css/controller";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import ArrowNavigation from "@werner/App/Components/XngCarousel/ArrowNavigation";
import { xSwiperStyle } from "@werner/App/Components/XngCarousel/styles";
import { color } from "@werner/XDL/Utilities";
import { mergeWithCss } from "@werner/XDL/Utilities/mergeWithCss";
import pick from "lodash/pick";
import React, { FC, isValidElement, useState } from "react";
import { css } from "styled-components";

export type Props = SwiperProps & {
    extendedStyles?: SwiperStyle;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}
            &.with-padding {
                .swiper {
                    margin: -15px;
                    box-sizing: border-box;
                    .swiper-wrapper {
                        padding: 15px;
                        box-sizing: border-box;
                    }
                }
            }

            .xng-pagination-container {
                &:not(.visible) {
                    display: none;
                }
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-top: 30px;
                gap: 16px;
                & > div,
                & > button {
                    margin: 0;
                }
            }

            .xng-pagination-container > .swiper-scrollbar.swiper-scrollbar-horizontal {
                height: 10px;
                width: 100%;
                position: relative;
                top: unset;
                right: unset;
                bottom: unset;
                left: unset;
                transform: initial;
                background: ${color("common.ui2")};
                border-radius: 0;
                & > .swiper-scrollbar-drag {
                    background: ${color("primary")};
                    border-radius: 0;
                }
            }

            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                gap: 8px 4px;

                &.swiper-pagination-lock {
                    display: none;
                }

                .swiper-pagination-bullet {
                    width: 10px;
                    height: 10px;
                    margin: 0;
                    background: ${color("common.white")};
                    border: 1px solid ${color("common.ui4")};
                    border-radius: 0;
                    opacity: 1;
                    &:hover:not(.swiper-pagination-bullet-active) {
                        background: ${color("common.ui8")};
                        border-color: ${color("common.ui8")};
                    }
                    &.swiper-pagination-bullet-active {
                        width: 10px;
                        height: 10px;
                        background: ${color("common.ui9")};
                        border: 1px solid ${color("common.ui9")};
                    }
                }
            }
        `,
    },
};

const XngCarousel: FC<Props> = props => {
    const {
        children,
        className,
        extendedStyles,
        slidesPerView = "auto",
        navigation = true,
        simulateTouch = false,
        spaceBetween = 20,
        pagination = false,
        scrollbar = false,
        watchOverflow = true,
        updateOnWindowResize = true,
        centerInsufficientSlides = true,
        draggable = true,
        freeMode = false,
        ...swiperProps
    } = props;
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));

    let scrollbarOptions;
    if (scrollbar) {
        scrollbarOptions = typeof scrollbar === "boolean" ? { draggable: true, el: ".swiper-scrollbar" } : scrollbar;
    }

    return (
        <StyledWrapper {...styles.container} className={`carousel-main-wrapper ${className || ""}`}>
            <Swiper
                {...swiperProps}
                slidesPerView={slidesPerView}
                navigation={navigation}
                simulateTouch={simulateTouch}
                spaceBetween={spaceBetween}
                pagination={pagination}
                scrollbar={scrollbarOptions}
                watchOverflow={watchOverflow}
                updateOnWindowResize={updateOnWindowResize}
                centerInsufficientSlides={centerInsufficientSlides}
                freeMode={freeMode}
                modules={[Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]}
            >
                {navigation && !scrollbar && <ArrowNavigation arrowType="prev" showOnMobile={true} />}
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props as any, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                        </SwiperSlide>
                    );
                })}

                {(pagination || navigation || scrollbar) && (
                    <StyledWrapper className={`xng-pagination-container ${scrollbar ? "visible" : ""}`}>
                        {/* {swiperProps.navigation && <ArrowNavigation arrowType="prev" showOnMobile={true} />} */}
                        {pagination && <div className="swiper-pagination" />}
                        {scrollbar && <div className="swiper-scrollbar" />}
                        {/* {swiperProps.navigation && <ArrowNavigation arrowType="next" showOnMobile={true} />} */}
                    </StyledWrapper>
                )}
                {navigation && !scrollbar && <ArrowNavigation arrowType="next" showOnMobile={true} />}
            </Swiper>
        </StyledWrapper>
    );
};

export default XngCarousel;
